/* Lumi Threads shared behaviour. Feature scripts (cart.js, search.js, product.js,
   collection.js) build on the helpers exported on window.theme. */

(() => {
  const FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary';

  function debounce(fn, wait = 200) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  }

  function fetchConfig(type = 'json') {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
    };
  }

  let releaseFocus = null;

  function trapFocus(container, initial) {
    removeTrapFocus();
    const focusables = () => Array.from(container.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null || el === document.activeElement);
    const onKeydown = (event) => {
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeydown);
    releaseFocus = () => document.removeEventListener('keydown', onKeydown);
    (initial || focusables()[0] || container).focus({ preventScroll: true });
  }

  function removeTrapFocus(returnTo) {
    if (releaseFocus) releaseFocus();
    releaseFocus = null;
    if (returnTo && typeof returnTo.focus === 'function') returnTo.focus({ preventScroll: true });
  }

  function lockScroll(locked) {
    document.body.classList.toggle('scroll-locked', locked);
  }

  function formatMoney(cents) {
    const format = (window.themeSettings && window.themeSettings.moneyFormat) || '${{amount}}';
    const value = (Number(cents) / 100).toFixed(2);
    const withCommas = value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace(/{{\s*amount_no_decimals\s*}}/, withCommas.split('.')[0])
      .replace(/{{\s*amount_with_comma_separator\s*}}/, value.replace('.', ','))
      .replace(/{{\s*amount\s*}}/, withCommas);
  }

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = Number(count) === 0;
    });
    document.querySelectorAll('[data-cart-count-label]').forEach((el) => {
      const template = Number(count) === 1 ? el.dataset.one : el.dataset.other;
      if (template) el.textContent = template.replace('__COUNT__', count);
    });
  }

  document.addEventListener('cart:updated', (event) => {
    const cart = event.detail && event.detail.cart;
    if (cart && typeof cart.item_count === 'number') updateCartCount(cart.item_count);
  });

  window.theme = { debounce, fetchConfig, trapFocus, removeTrapFocus, lockScroll, formatMoney, updateCartCount };

  /* Drawer: shared open/close for any .drawer element. */
  class ThemeDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) this.close();
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });
    }

    open(trigger) {
      if (this.classList.contains('is-open')) return;
      this.trigger = trigger || document.activeElement;
      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        lockScroll(true);
        const panel = this.querySelector('.drawer__panel') || this;
        trapFocus(panel, this.querySelector('[data-initial-focus]'));
      });
      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
      removeTrapFocus(this.trigger);
    }
  }
  if (!customElements.get('theme-drawer')) customElements.define('theme-drawer', ThemeDrawer);

  /* Header: sticky state and desktop dropdowns built on <details>. */
  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.dropdowns = Array.from(this.querySelectorAll('details[data-dropdown]'));
      this.onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 8);
      this.updateOverlay = () => {
        const main = document.getElementById('MainContent');
        const first = main && Array.from(main.querySelectorAll(':scope > .shopify-section')).find((el) => el.offsetHeight > 0);
        const overlay = this.dataset.overlay === 'true' && Boolean(first && first.querySelector('.hero, .holiday-hero'));
        document.documentElement.classList.toggle('header-overlay', overlay);
      };
      this.updateOverlay();
      document.addEventListener('shopify:section:load', this.updateOverlay);
      document.addEventListener('shopify:section:unload', () => setTimeout(this.updateOverlay));
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();

      const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.dropdowns.forEach((details) => {
        const summary = details.querySelector('summary');
        summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
        details.addEventListener('toggle', () => {
          summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
          if (details.open) this.dropdowns.filter((d) => d !== details).forEach((d) => d.removeAttribute('open'));
        });
        if (canHover.matches) {
          let leaveTimer;
          details.addEventListener('mouseenter', () => {
            clearTimeout(leaveTimer);
            details.setAttribute('open', '');
          });
          details.addEventListener('mouseleave', () => {
            leaveTimer = setTimeout(() => details.removeAttribute('open'), 120);
          });
        }
        details.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && details.open) {
            details.removeAttribute('open');
            summary.focus();
          }
        });
        details.addEventListener('focusout', (event) => {
          if (!details.contains(event.relatedTarget)) details.removeAttribute('open');
        });
      });

      document.addEventListener('click', (event) => {
        this.dropdowns.forEach((d) => {
          if (!d.contains(event.target)) d.removeAttribute('open');
        });
      });

      const setHeight = () => document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
      setHeight();
      window.addEventListener('resize', debounce(setHeight, 100));
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }
  }
  if (!customElements.get('site-header')) customElements.define('site-header', SiteHeader);

  /* Search modal: a native <dialog>. search.js adds live suggestions. */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open-search]');
    if (!opener) return;
    const dialog = document.getElementById('SearchModal');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    event.preventDefault();
    dialog.showModal();
    lockScroll(true);
    const input = dialog.querySelector('input[type="search"]');
    if (input) input.focus();
    dialog.addEventListener('close', () => {
      lockScroll(false);
      opener.focus();
    }, { once: true });
  });

  document.addEventListener('click', (event) => {
    const dialog = event.target.closest('dialog');
    if (dialog && event.target === dialog) dialog.close();
    const closer = event.target.closest('[data-dialog-close]');
    if (closer) closer.closest('dialog')?.close();
  });

  /* Drawer openers: any [data-drawer-open="ID"] button opens that drawer. */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (!opener) return;
    const drawer = document.getElementById(opener.dataset.drawerOpen);
    if (!drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    drawer.open(opener);
  });

  /* Announcement bar rotation. */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-announcement]'));
      if (this.slides.length < 2) return;
      this.index = 0;
      this.paused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.toggle = this.querySelector('[data-announcement-toggle]');
      this.addEventListener('mouseenter', () => (this.hovered = true));
      this.addEventListener('mouseleave', () => (this.hovered = false));
      this.addEventListener('focusin', () => (this.hovered = true));
      this.addEventListener('focusout', () => (this.hovered = false));
      if (this.toggle) {
        this.toggle.hidden = false;
        this.syncToggle();
        this.toggle.addEventListener('click', () => {
          this.paused = !this.paused;
          this.syncToggle();
        });
      }
      this.timer = setInterval(() => {
        if (this.paused || this.hovered) return;
        this.show((this.index + 1) % this.slides.length);
      }, 5000);
    }

    syncToggle() {
      this.toggle.setAttribute('aria-label', this.paused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel);
      this.toggle.setAttribute('aria-pressed', this.paused ? 'true' : 'false');
    }

    show(i) {
      this.slides[this.index].classList.remove('is-active');
      this.slides[this.index].setAttribute('aria-hidden', 'true');
      this.index = i;
      this.slides[i].classList.add('is-active');
      this.slides[i].setAttribute('aria-hidden', 'false');
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }
  }
  if (!customElements.get('announcement-bar')) customElements.define('announcement-bar', AnnouncementBar);

  /* Live countdown for the holiday hero. Server renders the starting text. */
  class HolidayCountdown extends HTMLElement {
    connectedCallback() {
      const end = this.dataset.end;
      if (!end) return;
      const [y, m, d] = end.split('-').map(Number);
      this.endsAt = new Date(y, m - 1, d, 23, 59, 59);
      this.output = this.querySelector('[data-countdown-output]');
      if (!this.output) return;
      this.tick();
      this.timer = setInterval(() => this.tick(), 60000);
    }

    tick() {
      const ms = this.endsAt - new Date();
      if (ms <= 0) {
        this.hidden = true;
        clearInterval(this.timer);
        return;
      }
      const days = Math.floor(ms / 86400000);
      const hours = Math.floor((ms % 86400000) / 3600000);
      if (days >= 1) {
        const template = days === 1 ? this.dataset.one : this.dataset.other;
        if (template) this.output.textContent = template.replace('__COUNT__', days);
      } else {
        const template = this.dataset.hours;
        if (template) this.output.textContent = template.replace('__COUNT__', Math.max(1, hours));
      }
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }
  }
  if (!customElements.get('holiday-countdown')) customElements.define('holiday-countdown', HolidayCountdown);
})();
